import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';
import type { SsoEntity, SsoEntityList, SsoOpenResponse } from '@mnc/contracts';
import { api, ApiError } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, EmptyState } from '../../components/ui';

/**
 * Consultant single sign-on, the central side: central hands the browser a 60-second token and sends it to the entity,
 * which signs the person in with its own session (ADR-007). The token never touches the entity's login form.
 */

export function useOpenEntity() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (entity_slug: string) => api.post<SsoOpenResponse>('/api/sso/open', { entity_slug }),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.error(describeError(e, t)),
  });
}

/** One row per entity I may open, with why one cannot be opened. Shared by the home widget and the hand-off page. */
export function EntityRow({ e, onOpen, busy }: { e: SsoEntity; onOpen: (slug: string) => void; busy: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" dir="auto">{pick(i18n.language, e.entity.name_en, e.entity.name_ar)}</div>
        <div className="text-xs text-neutral-500">
          {t(`people.roles.${e.role}`)}
          {e.end_date && e.days_to_end !== null && e.days_to_end <= 30 && <span className="ms-2 text-amber-600">{t('people.endsIn', { count: e.days_to_end })}</span>}
        </div>
      </div>
      {e.can_open ? (
        <button type="button" className="btn-primary px-2 py-1 text-xs" disabled={busy} onClick={() => onOpen(e.entity.slug)}><ExternalLink size={12} /> {t('sso.open')}</button>
      ) : (
        <Badge tone="neutral">{t(`sso.blocked.${e.blocked_reason ?? 'entity_inactive'}`)}</Badge>
      )}
    </li>
  );
}

export const parseSsoStartSearch = (raw: Record<string, unknown>): { entity?: string } => ({ entity: typeof raw.entity === 'string' && /^[a-z0-9_-]{1,64}$/.test(raw.entity) ? raw.entity : undefined });

/** `/sso/start?entity=<slug>`: where an entity's login page sends a consultant. Signed in, it opens the entity at once. */
export function SsoStartPage() {
  const { t, i18n } = useTranslation();
  const { entity } = parseSsoStartSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const mine = useQuery({ queryKey: ['sso', 'entities'], queryFn: () => api.get<SsoEntityList>('/api/sso/entities') });
  const open = useOpenEntity();
  const [problem, setProblem] = useState<string | null>(null);
  const tried = useRef(false);
  useEffect(() => {
    if (!entity || !mine.data || tried.current) return;
    tried.current = true;
    const target = mine.data.items.find((e) => e.entity.slug === entity);
    if (!target) return setProblem(t('sso.notAllocated', { entity }));
    if (!target.can_open) return setProblem(t(`sso.blocked.${target.blocked_reason ?? 'entity_inactive'}`));
    open.mutate(entity, { onError: (e) => setProblem(e instanceof ApiError ? e.message : t('errors.generic')) });
  }, [entity, mine.data, open, t]);

  return (
    <div className="mx-auto max-w-lg">
      <div className="card p-6">
        <h1 className="text-lg font-semibold">{t('sso.startTitle')}</h1>
        {!entity && <p className="mt-2 text-sm text-neutral-500">{t('sso.noEntityAsked')}</p>}
        {entity && !problem && <p className="mt-2 text-sm text-neutral-500">{t('sso.opening', { entity: pick(i18n.language, mine.data?.items.find((e) => e.entity.slug === entity)?.entity.name_en ?? entity, mine.data?.items.find((e) => e.entity.slug === entity)?.entity.name_ar ?? entity) })}</p>}
        {problem && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200" role="alert">{problem}</p>}
        <h2 className="mt-4 text-sm font-semibold">{t('sso.myEntities')}</h2>
        {mine.data?.items.length === 0 && <EmptyState>{t('sso.none')}</EmptyState>}
        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">{mine.data?.items.map((e) => <EntityRow key={e.allocation_id} e={e} busy={open.isPending} onOpen={(slug) => open.mutate(slug)} />)}</ul>
        <div className="mt-4"><Link to="/" className="btn-secondary">{t('app.back')}</Link></div>
      </div>
    </div>
  );
}
